import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';

import Tab from 'reusable/tab';
import Pagination from 'reusable/pagination';

import AllSubmissionsTable from './Table/AllSubmissionsTable';
import ExceedAlcoholLimitsTable from './Table/ExceedAlcoholLimitsTable';
import RecentSubmissionsTable from './Table/RecentSubmissionsTable';
import Toolbar from './Toolbar';
import { PAGE_SIZE } from 'config/constant';
import { useTranslation } from 'react-i18next';



const AlcoholManagement: React.FC = () => {
  const { t } = useTranslation('alcohol');
  const tabsData = [
    { label: `${t('tab.all')}`, value: 'allsubmission' },
    { label: `${t('tab.limit')}`, value: 'exceedalcohollimits' },
    { label: `${t('tab.recent')}`, value: 'recentsubmissions' },
  ];
  const today = new Date().toISOString().slice(0, 10);
  const [activeTab, setActiveTab] = useState<string>(tabsData[0].value);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [dateValue, setDateValue] = useState(today);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataAlcoholTest, setDataAlcoholTest] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataForExport, setDataForExport] = useState<any[]>([]);

  const handleTabClick = (value: string) => {
    setActiveTab(value);
    setCurrentPage(1);
  };

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleDateChange = (value: string) => {
    setDateValue(value);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  const fetchProfilePicture = async (id: number) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${id}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(`Failed to fetch profile picture for driver ${id}`, error);
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let apiUrl = '/v1/alcohol_tests/';
      const date = new Date(dateValue); 
      const startDate = new Date(date);
      startDate.setDate(startDate.getDate() - 1);
      startDate.setUTCHours(15, 0, 0, 0);
      const startTime = startDate.toISOString().replace(/:\d+\.\d+Z$/, ':00.000000Z');  
      const endDate = new Date(date);
      endDate.setUTCHours(14, 59, 59, 999);
      const endTime = endDate.toISOString().replace(/Z$/, '999Z'); 

      let fetchedData = [];

      if (activeTab === 'exceedalcohollimits') {
        apiUrl += `?data__gte=0.15&order_by=${sortValue}&page=${currentPage}&page_size=${PAGE_SIZE}&search=${searchValue}&testing_time__gte=${encodeURIComponent(startTime)}&testing_time__lte=${encodeURIComponent(endTime)}`;
      } else if (activeTab === 'recentsubmissions') {
        apiUrl = `/v1/drivers/recent_submission/?alcoholtest__testing_time__gte=${encodeURIComponent(startTime)}&alcoholtest__testing_time__lte=${encodeURIComponent(endTime)}&order_by=${sortValue}&search=${searchValue}&page=${currentPage}`;
      } else {
        apiUrl += `?order_by=${sortValue}&page=${currentPage}&page_size=${PAGE_SIZE}&search=${searchValue}&testing_time__gte=${encodeURIComponent(startTime)}&testing_time__lte=${encodeURIComponent(endTime)}`;
      }

      const response = await axiosInstance.get(apiUrl);
      fetchedData = response.data.results;

      if (activeTab === 'recentsubmissions') {
        // Fetch profile pictures for 'recentsubmissions'
        const updatedRecords = await Promise.all(
          fetchedData.map(async (record: any) => {
            const profilePicture = await fetchProfilePicture(record.id);
            return { ...record, profile_picture: profilePicture };
          })
        );
        setRecords(updatedRecords);
      } else {
        // Fetch profile pictures for other tabs
        const updatedDataAlcoholTest = await Promise.all(
          fetchedData.map(async (item: any) => {
            const profilePicture = await fetchProfilePicture(item.user.id);
            return { ...item, user: { ...item.user, profile_picture: profilePicture } };
          })
        );
        setDataAlcoholTest(updatedDataAlcoholTest);
      }

      setDataForExport(fetchedData);
      setCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [searchValue, sortValue, dateValue, currentPage, activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      {/* Tab Section */}
      <Tab tabs={tabsData} onTabClick={handleTabClick} />

      {/* Toolbar Section */}
      <div className="flex flex-row justify-between w-full">
        <Toolbar
          searchValue={searchValue}
          sortValue={sortValue}
          dateValue={dateValue}
          currentPage={currentPage}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onDateChange={handleDateChange}
          onPageChange={handlePageChange}
          DataForExport={dataForExport}
          activetab={activeTab}
        />
      </div>

      {/* Loading Indicator */}
      {loading ? (
        <div
          className="flex justify-center items-center"
          style={{ height: '73vh' }}
        >
          <div>Loading...</div>
        </div>
      ) : (
        <div>
          {/* Table Section */}
          {activeTab === 'allsubmission' && (
            <AllSubmissionsTable submissions={dataAlcoholTest} />
          )}
          {activeTab === 'exceedalcohollimits' && (
            <ExceedAlcoholLimitsTable submissions={dataAlcoholTest} />
          )}
          {activeTab === 'recentsubmissions' && (
            <RecentSubmissionsTable records={records} />
          )}
        </div>
      )}

      {/* Pagination Section */}
      <Pagination
        allRows={count}
        rowsPerPage={PAGE_SIZE}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default AlcoholManagement;